export const getDiseaseItemColumnHeader = (selectedFilter: string) => {
  switch (selectedFilter) {
    case 'diseaseUMLSCUI':
      return 'Disease CUI';
    case 'MSH':
      return 'MESH';
    case 'name':
      return 'Disease Name';
    default:
      return selectedFilter;
  }
};

export const getVariantItemColumnHeader = (selectedFilter: string) => {
  switch (selectedFilter) {
    case 'strID':
      return 'VARIANT rs number';
    case 'chromocoord':
      return 'Chrom. position';
    case 'proteinStrID':
      return 'Protein mutation';
    case 'hgvsc':
      return 'HGVSc';
    case 'hgvsp':
      return 'HGVSp';
    default:
      return selectedFilter;
  }
};

export const getGeneItemColumnHeader = (selectedFilter: string) => {
  switch (selectedFilter) {
    case 'symbol':
      return 'Gene Symbol';
    case 'ensemblID':
      return 'Ensembl ID';
    case 'uniprotID':
      return 'Uniprot ID';
    case 'geneID':
      return 'Gene ID';
    case 'name':
      return 'Gene Name';
    default:
      return selectedFilter;
  }
};

export const getGeneSecondColumnHeader = (selectedFilter: string) => {
  if (selectedFilter === 'geneID') {
    return 'Gene Symbol';
  }
  return 'Gene ID';
};

export const getGeneThirdColumnHeader = (selectedFilter: string) => {
  if (selectedFilter === 'ensemblID' || selectedFilter === 'uniprotID' || selectedFilter === 'name') {
    return 'Gene Symbol';
  }
  return 'ENSEMBL ID';
};

export const getChemicalColumnHeader = (selectedFilter: string) => {
  if (selectedFilter === 'CHEBI') {
    return 'CHEBI ID';
  } else if (selectedFilter === 'DRUGBANK') {
    return 'DRUGBANK ID';
  } else if (selectedFilter === 'MESH') {
    return 'MESH ID';
  } else if (selectedFilter === 'CHEMBL') {
    return 'CHEMBL ID';
  } else if (selectedFilter === 'PUBCHEM') {
    return 'PUBCHEM ID';
  } else if (selectedFilter === 'name') {
    return 'Chemical Name';
  } else if (selectedFilter === 'normalizedName') {
    return 'UMLS Name';
  }
};

export const getResultColumnValue = (
  searchType: string,
  selectedFilter: string,
  result: any,
  query: string,
) => {
  switch (searchType) {
    case 'DISEASES':
      return getDiseaseColumnValue(selectedFilter, result, query);
    case 'VARIANTS':
      return getVariantColumnValue(selectedFilter, result, query);
    case 'GENES':
      return getGeneColumnValue(selectedFilter, result);
    case 'CHEMICALS':
      return getCleanChemicalColumnValue(selectedFilter, result);
    default:
      return 'Not available';
  }
};

export const getDiseaseColumnValue = (selectedFilter: string, result: any, query: string) => {
  switch (selectedFilter) {
    case 'diseaseUMLSCUI':
      return Array.isArray(result.diseaseUMLSCUI)
        ? result.diseaseUMLSCUI.join(', ')
        : result.diseaseUMLSCUI || 'Not available';
    case 'MSH':
      return Array.isArray(result.msh) ? result.msh.join(', ') : result.msh || 'Not available';
    case 'OMIM':
      return Array.isArray(result.omim) ? result.omim.find(i => i === query) : result.omim || 'Not available';
    case 'NCI':
      return Array.isArray(result.nci) ? result.nci.join(', ') : result.nci || 'Not available';
    case 'ORDO':
      return Array.isArray(result.ordo) ? result.ordo.join(', ') : result.ordo || 'Not available';
    case 'HPO':
      return Array.isArray(result.hpo) ? result.hpo.join(', ') : result.hpo || 'Not available';
    case 'MONDO':
      return Array.isArray(result.mondo)
        ? result.mondo.find(i => i === query)
        : result.mondo || 'Not available';
    case 'EFO':
      return Array.isArray(result.efo) ? result.efo.find(i => i === query) : result.efo || 'Not available';
    case 'name':
      return result.term || 'Not available';
    case 'normalizedName':
      return result.normalizedName || 'Not available';
    case 'DO':
      return Array.isArray(result.do) ? result.do.find(i => i === query) : result.do || 'Not available';
    default:
      return 'Not available';
  }
};

export const getVariantColumnValue = (selectedFilter: string, result: any, query: string) => {
  const getMatchingHgv = (value: any, input: string) => {
    if (Array.isArray(value)) {
      const matches = value.filter(v => String(v).includes(input));
      return matches.length > 0 ? matches.join(', ') : 'Not available';
    }
    if (typeof value === 'string') {
      return value.includes(input) ? value : value || 'Not available';
    }
    return 'Not available';
  };

  switch (selectedFilter) {
    case 'strID':
      return Array.isArray(result.strID) ? result.strID.join(', ') : result.strID || 'Not available';
    case 'chromocoord':
      return result.chromosome ? `${result.chromosome}:${result.coord}` : 'Not available';
    case 'proteinStrID': {
      const normalizedQuery = query.toLowerCase().replace(/\s+/g, ' ').trim();

      const matchingThreeLetter =
        Array.isArray(result.threeletterID) &&
        result.threeletterID.filter(id =>
          id.toLowerCase().replace(/\s+/g, ' ').trim().includes(normalizedQuery),
        );

      const matchingOneLetter =
        Array.isArray(result.oneletterID) &&
        result.oneletterID.filter(id =>
          id.toLowerCase().replace(/\s+/g, ' ').trim().includes(normalizedQuery),
        );

      if (matchingThreeLetter && matchingThreeLetter.length > 0) {
        return matchingThreeLetter.join(', ');
      } else if (matchingOneLetter && matchingOneLetter.length > 0) {
        return matchingOneLetter.join(', ');
      }

      return 'Not available';
    }
    case 'hgvsc':
      return getMatchingHgv(result.hgvsc, query);
    case 'hgvsp':
      return getMatchingHgv(result.hgvsp, query);
    default:
      return 'Not available';
  }
};

export const getGeneColumnValue = (selectedFilter: string, result: any) => {
  switch (selectedFilter) {
    case 'symbol':
      return Array.isArray(result.symbol) ? result.symbol.join(', ') : result.symbol || 'Not available';
    case 'geneID':
      return Array.isArray(result.geneID) ? result.geneID.join(', ') : result.geneID || 'Not available';
    case 'ensemblID':
      return Array.isArray(result.ensembl_ids)
        ? result.ensembl_ids.join(', ')
        : result.ensembl_ids || 'Not available';
    case 'uniprotID':
      return Array.isArray(result.proteinStrIDs)
        ? result.proteinStrIDs.join(', ')
        : result.proteinStrIDs || 'Not available';
    case 'name':
      return result.term || result.matchedText || 'Not available';
    default:
      return 'Not available';
  }
};

export const getChemicalColumnValue = (selectedFilter: string, result: any) => {
  switch (selectedFilter) {
    case 'CHEBI':
      return Array.isArray(result.chebi[0])
        ? result.chebi[0][0].join(', ')
        : result.chebi[0] || 'Not available';
    case 'DRUGBANK':
      return Array.isArray(result.drugbank[0])
        ? result.drugbank[0][0].join(', ')
        : result.drugbank[0] || 'Not available';
    case 'MESH':
      return Array.isArray(result.mesh[0]) ? result.mesh[0][0].join(', ') : result.mesh[0] || 'Not available';
    case 'CHEMBL':
      return Array.isArray(result.chembl[0])
        ? result.chembl[0][0].join(', ')
        : result.chembl[0] || 'Not available';
    case 'PUBCHEM':
      return Array.isArray(result.pubchem[0])
        ? result.pubchem[0][0].join(', ')
        : result.pubchem[0] || 'Not available';
    case 'name':
      return result.term || 'Not available';
    default:
      return 'Not available';
  }
};

export const getCleanChemicalColumnValue = (selectedFilter: string, result: any) => {
  // the raw data non-chemId values can be like __{id}__someName
  // we need to clean it up to just {id}
  const label = getChemicalColumnValue(selectedFilter, result);
  let clean = typeof label === 'string' ? label.replace(/^_+/, '') : label;
  clean = typeof clean === 'string' ? clean.replace(/_+$/, '') : clean;
  // Remove anything after matching "___"
  clean = typeof clean === 'string' ? clean.split('___')[0] : clean;
  return clean || 'Not available';
};

export const getResultName = (result: any, searchType: string, selectedFilter: string) => {
  switch (searchType) {
    case 'DISEASES':
      if (selectedFilter !== 'name') {
        return result.name || 'Not available';
      }
      return result.normalizedId || 'Not available';
    case 'VARIANTS':
      return result.geneSymbol;
    case 'GENES':
      if (selectedFilter !== 'name') {
        return result.geneID || 'Not available';
      }
      return result.normalizedId || 'Not available';
    case 'CHEMICALS':
      if (selectedFilter !== 'name') {
        return result.chemPrefName || 'Not available';
      }
      return result.normalizedId || result.chemID || 'Not available';
    default:
      return 'Not available';
  }
};
